import React from "react";
import { useAuth } from "@/context/AuthContext";
import { fmtEuro } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2 } from "lucide-react";

export const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const KEYS = MESI.map((_, i) => String(i + 1).padStart(2, "0"));

export const totOre = (a) => KEYS.reduce((s, k) => s + (parseFloat(a.ore?.[k]) || 0), 0);

export function AnniOreField({ value, onChange }) {
  const { user } = useAuth();
  const canEcon = user?.can_economics;
  const anni = Array.isArray(value) ? value : [];
  const upd = (i, patch) => onChange(anni.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const setOra = (i, k, v) => upd(i, { ore: { ...(anni[i].ore || {}), [k]: v === "" ? "" : parseFloat(v) } });
  const addYear = () => {
    const last = anni.length ? Math.max(...anni.map((a) => parseInt(a.anno) || 0)) : new Date().getFullYear() - 1;
    const prev = anni.find((a) => parseInt(a.anno) === last);
    onChange([...anni, { anno: String(last + 1), costo_orario: prev?.costo_orario ?? "", ore: {} }]);
  };
  const dup = (a) => anni.filter((x) => x.anno === a.anno).length > 1;

  return (
    <div className="mt-1 space-y-3" data-testid="anni-ore-field">
      {anni.length === 0 && <p className="text-sm text-slate-400">Nessun anno inserito.</p>}
      {anni.map((a, i) => (
        <div key={i} className="rounded-lg border border-slate-200 p-3" data-testid={`anno-block-${a.anno}`}>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <span className="text-[11px] font-semibold uppercase text-slate-500">Anno</span>
              <Input type="number" value={a.anno} onChange={(e) => upd(i, { anno: e.target.value })} className={`mt-1 w-24 ${dup(a) ? "border-rose-500" : ""}`} data-testid={`anno-input-${i}`} />
            </div>
            {canEcon && (
              <div>
                <span className="text-[11px] font-semibold uppercase text-slate-500">Costo orario (€)</span>
                <Input type="number" value={a.costo_orario ?? ""} onChange={(e) => upd(i, { costo_orario: e.target.value === "" ? "" : parseFloat(e.target.value) })} className="mt-1 w-32" data-testid={`anno-costo-${i}`} />
              </div>
            )}
            <div className="ml-auto text-right text-sm">
              <div className="text-slate-500">Totale: <span className="font-semibold text-slate-900" data-testid={`anno-tot-${i}`}>{totOre(a)}h</span></div>
              {canEcon && a.costo_orario ? <div className="text-xs text-slate-400">{fmtEuro(totOre(a) * a.costo_orario)}</div> : null}
            </div>
            <button type="button" onClick={() => onChange(anni.filter((_, j) => j !== i))} className="rounded p-1.5 text-slate-500 hover:bg-rose-50 hover:text-rose-600" data-testid={`anno-del-${i}`}><Trash2 className="h-4 w-4" /></button>
          </div>
          {dup(a) && <p className="mt-1 text-xs text-rose-600">Anno già presente</p>}
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[780px] text-xs">
              <thead><tr>{MESI.map((m) => <th key={m} className="px-1 pb-1 text-center font-semibold uppercase text-slate-500">{m}</th>)}</tr></thead>
              <tbody><tr>{KEYS.map((k) => (
                <td key={k} className="px-0.5">
                  <Input type="number" value={a.ore?.[k] ?? ""} onChange={(e) => setOra(i, k, e.target.value)} className="h-8 px-1 text-center text-xs" data-testid={`ore-${i}-${k}`} />
                </td>
              ))}</tr></tbody>
            </table>
          </div>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={addYear} data-testid="add-anno-btn"><Plus className="mr-1.5 h-4 w-4" />Aggiungi anno</Button>
    </div>
  );
}
